import { useState } from "react";
import { Button, Space, Popconfirm, Tooltip } from "antd";
import { notify } from "@/shared/utils/notify";
import { 
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  RocketOutlined,
  CloseCircleOutlined,
  ShoppingOutlined
} from "@ant-design/icons";
import { IOrder } from "../../../redux/@types";
import { ordersService } from "../../../shared/services";

interface ActionButton {
  id: number;
  name: string;
  icon: JSX.Element;
  color: string;
  tooltip: string;
  requiresConfirm?: boolean;
}

interface OrderStatusActionsProps {
  order: IOrder;
  orderStatus: Array<{
    id: number;
    name: string;
    description: string;
    status_code: string;
    color: string;
  }>;
  onStatusChanged?: () => void;
}

export const OrderStatusActions = ({
  order,
  orderStatus,
  onStatusChanged,
}: OrderStatusActionsProps) => {
  const [loading, setLoading] = useState(false);

  const updateOrderStatus = async (newStatusId: number, statusName: string) => {
    setLoading(true);
    try {
      await ordersService.patch(order.id, {
        order_status_id: newStatusId,
      });
      notify.success(`Estado actualizado a: ${statusName}`);
      if (onStatusChanged) {
        onStatusChanged();
      }
    } catch (err: any) {
      notify.error(err.message || "Error al actualizar el estado");
    } finally {
      setLoading(false);
    }
  };

  // Definir los estados según el flujo típico de órdenes
  const getNextActions = (): ActionButton[] => {
    const currentStatusId = order.order_status_id;
    const actions: ActionButton[] = [];

    // Estado 1: Pendiente -> puede ir a Confirmado (2)
    if (currentStatusId === 1) {
      const confirmadoStatus = orderStatus.find((s) => s.id === 2);
      if (confirmadoStatus) {
        actions.push({
          id: 2,
          name: confirmadoStatus.name,
          icon: <CheckCircleOutlined style={{ fontSize: 16 }} />,
          color: "#E95735",
          tooltip: "Confirmar pedido",
        });
      }
    }

    // Estado 2: Confirmado -> puede ir a En Proceso (3)
    if (currentStatusId === 2) {
      const enProcesoStatus = orderStatus.find((s) => s.id === 3);
      if (enProcesoStatus) {
        actions.push({
          id: 3,
          name: enProcesoStatus.name,
          icon: <ClockCircleOutlined style={{ fontSize: 16 }} />,
          color: "#FF8C42",
          tooltip: "Marcar como en proceso",
        });
      }
    }

    // Estado 3: En Proceso -> puede ir a En Preparación (5)
    if (currentStatusId === 3) {
      const enPreparacionStatus = orderStatus.find((s) => s.id === 5);
      if (enPreparacionStatus) {
        actions.push({
          id: 5,
          name: enPreparacionStatus.name,
          icon: <ShoppingOutlined style={{ fontSize: 16 }} />,
          color: "#E95735",
          tooltip: "Marcar como en preparación",
        });
      }
    }

    // Estado 5: En Preparación -> puede ir a En Camino (8)
    if (currentStatusId === 5) {
      const enCaminoStatus = orderStatus.find((s) => s.id === 8);
      if (enCaminoStatus) {
        actions.push({
          id: 8,
          name: enCaminoStatus.name,
          icon: <RocketOutlined style={{ fontSize: 16 }} />,
          color: "#FF8C42",
          tooltip: "Marcar como en camino",
        });
      }
    }

    // Estado 8: En Camino -> puede ir a Entregado (9)
    if (currentStatusId === 8) {
      const entregadoStatus = orderStatus.find((s) => s.id === 9);
      if (entregadoStatus) {
        actions.push({
          id: 9,
          name: entregadoStatus.name,
          icon: <CheckCircleOutlined style={{ fontSize: 16 }} />,
          color: "#52c41a",
          tooltip: "Marcar como entregado",
        });
      }
    }

    // Opción para cancelar (si no está cancelado (10) o entregado (9))
    if (currentStatusId !== 9 && currentStatusId !== 10) {
      const canceladoStatus = orderStatus.find((s) => s.id === 10);
      if (canceladoStatus) {
        actions.push({
          id: 10,
          name: canceladoStatus.name,
          icon: <CloseCircleOutlined style={{ fontSize: 16 }} />,
          color: "#ff4d4f",
          tooltip: "Cancelar orden",
          requiresConfirm: true,
        });
      }
    }

    return actions;
  };

  const actions = getNextActions();

  if (actions.length === 0) {
    return null;
  }

  return (
    <Space size="small">
      {actions.map((action) => {
        const button = (
          <Button
            key={action.id}
            type={action.id === 10 ? "default" : "primary"}
            icon={action.icon}
            size="middle"
            loading={loading}
            style={{
              backgroundColor: action.id !== 10 ? action.color : undefined,
              borderColor: action.color,
              height: 32,
              width: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              borderRadius: 6,
            }}
            onClick={() => !action.requiresConfirm && updateOrderStatus(action.id, action.name)}
          />
        );

        if (action.requiresConfirm) {
          return (
            <Popconfirm
              key={action.id}
              title={`¿Estás seguro de cambiar el estado a ${action.name}?`}
              onConfirm={() => updateOrderStatus(action.id, action.name)}
              okText="Sí"
              cancelText="No"
            >
              <Tooltip title={action.tooltip}>
                {button}
              </Tooltip>
            </Popconfirm>
          );
        }

        return (
          <Tooltip key={action.id} title={action.tooltip}>
            {button}
          </Tooltip>
        );
      })}
    </Space>
  );
};


